import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Prompt, Link as RouterLink } from 'react-router-dom'
import html2canvas from 'html2canvas'
import {
	Box,
	Heading,
	Button,
	ButtonGroup,
	Text,
	Link,
	Textarea,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
	useClipboard,
	VStack,
	HStack,
	Badge,
	useColorModeValue,
} from '@chakra-ui/react'

import { CollectionContext } from '../Components/CollectionContext'
import Step0 from '../Components/RankingSteps/Step0'
import Step1 from '../Components/RankingSteps/Step1'
import Step2 from '../Components/RankingSteps/Step2'
import Step3 from '../Components/RankingSteps/Step3'
import RankingSaveSlots from '../Components/RankingSaveSlots'
import {
	createInitialRankStateFromCollectionIds,
	buildItemsRecordFromCollection,
	serializeRankExport,
	rankStateFromV2,
	gameObjectId,
	buildCollectionStubsForRanking,
} from '../ranking/rankingState'

function collectionKeyFromGames(collection) {
	return collection
		.map(gameObjectId)
		.filter(Boolean)
		.sort()
		.join(',')
}

export default function Ranking() {
	const [collection, setCollection] = useState([])
	const collectionKey = useMemo(() => collectionKeyFromGames(collection), [collection])

	const [rankState, setRankState] = useState(() => createInitialRankStateFromCollectionIds([]))
	const [itemsById, setItemsById] = useState({})
	const [rankCollectionKey, setRankCollectionKey] = useState('')

	const [currentStep, setCurrentStep] = useState(1)
	const [canMoveOn, setCanMoveOn] = useState(false)

	const { isOpen, onOpen, onClose } = useDisclosure()
	const {
		isOpen: savedSlotsOpen,
		onOpen: onOpenSavedSlots,
		onClose: onCloseSavedSlots,
	} = useDisclosure()

	const rankingJpegRef = useRef(null)
	const skipBeforeUnloadRef = useRef(false)
	const [isJpgExporting, setIsJpgExporting] = useState(false)

	const shouldWarnOnLeave = currentStep >= 2
	const leaveConfirmMessage =
		'You have a ranking in progress. If you leave now, unsaved changes will be lost.'

	useEffect(() => {
		if (!shouldWarnOnLeave) return
		const onBeforeUnload = (e) => {
			if (skipBeforeUnloadRef.current) return
			e.preventDefault()
			e.returnValue = ''
		}
		window.addEventListener('beforeunload', onBeforeUnload)
		return () => window.removeEventListener('beforeunload', onBeforeUnload)
	}, [shouldWarnOnLeave])

	const exportJson = useMemo(() => JSON.stringify(serializeRankExport(rankState, itemsById), null, 4), [rankState, itemsById])
	const { hasCopied, onCopy } = useClipboard(exportJson)

	// Re-init tiers only when step-2 collection id-set differs from rankCollectionKey (new BGG load).
	// Avoid clearing rankCollectionKey on collectionKey changes without updating rank (breaks slot load).

	useEffect(() => {
		if (currentStep !== 2 || !collection.length) return
		if (rankCollectionKey === collectionKey) return
		const ids = collection.map(gameObjectId).filter(Boolean)
		setRankState(createInitialRankStateFromCollectionIds(ids))
		setItemsById((prev) => ({ ...buildItemsRecordFromCollection(collection), ...prev }))
		setRankCollectionKey(collectionKey)
	}, [currentStep, collection, collectionKey, rankCollectionKey])

	const onImportRank = useCallback(
		(v2) => {
			setRankState(rankStateFromV2(v2))
			setItemsById(v2.items && typeof v2.items === 'object' ? v2.items : {})
			setRankCollectionKey(collectionKey || `__import_${Date.now()}`)
			setCurrentStep(3)
		},
		[collectionKey]
	)

	const exportGetSnapshot = useCallback(() => serializeRankExport(rankState, itemsById), [rankState, itemsById])

	const handleDownloadJpg = useCallback(async () => {
		const node = rankingJpegRef.current
		if (!node) {
			console.warn('Ranking overview is not ready yet.')
			return
		}
		setIsJpgExporting(true)
		try {
			const w = node.scrollWidth
			const h = node.scrollHeight
			const canvas = await html2canvas(node, {
				scale: 2,
				backgroundColor: '#ffffff',
				useCORS: true,
				logging: false,
				width: w,
				height: h,
				windowWidth: w,
				windowHeight: h,
			})
			const blob = await new Promise((resolve, reject) => {
				canvas.toBlob(
					(b) => (b ? resolve(b) : reject(new Error('Could not create image.'))),
					'image/jpeg',
					0.92
				)
			})
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			const d = new Date()
			const pad = (n) => String(n).padStart(2, '0')
			a.href = url
			a.download = `bgg-ranking-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.jpg`
			a.rel = 'noopener'
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			URL.revokeObjectURL(url)
		} catch (e) {
			console.error(e)
		} finally {
			setIsJpgExporting(false)
		}
	}, [])

	const onProceedToRanking = useCallback(() => {
		setCurrentStep(2)
	}, [])

	const onLoadSlot = useCallback((v2) => {
		const rs = rankStateFromV2(v2)
		const items = v2.items && typeof v2.items === 'object' ? { ...v2.items } : {}
		const stubs = buildCollectionStubsForRanking(rs, items)
		const key = collectionKeyFromGames(stubs)
		setRankState(rs)
		setItemsById(items)
		setCollection(stubs)
		setRankCollectionKey(key)
		setCurrentStep(2)
		setCanMoveOn(true)
	}, [])

	const onLoadSlotFromSavedModal = useCallback(
		(v2) => {
			onLoadSlot(v2)
			onCloseSavedSlots()
		},
		[onLoadSlot, onCloseSavedSlots]
	)

	const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.300')
	const mainPanelBg = useColorModeValue('white', 'bgg.900')
	const bodyMuted = useColorModeValue('gray.600', 'bgg.muted')
	const exportModalMuted = useColorModeValue('gray.600', 'bgg.muted')

	return (
		<>
			<Prompt when={shouldWarnOnLeave} message={leaveConfirmMessage} />
			<VStack spacing={8} align="stretch">
				<VStack spacing={3} align="flex-start">
					<HStack spacing={3} flexWrap="wrap">
						<Heading size="lg">Ranking engine</Heading>
						<Badge variant="subtle" colorScheme="brand" fontSize="0.7rem" px={2} py={0.5} rounded="md">
							Beta
						</Badge>
					</HStack>
					<Text color={bodyMuted} fontSize="md" maxW="lg">
						Want to make a tier list? Perhaps decide what to keep and what to sell? Load your BGG game library and sort them out.{' '}
						<Link as={RouterLink} to="/how-to" color="brand.500" fontWeight="600">
							How to
						</Link>
					</Text>
				</VStack>

				<Box
					borderWidth="1px"
					borderColor={panelBorder}
					rounded="xl"
					p={{ base: 5, md: 8 }}
					bg={mainPanelBg}
				>
					<CollectionContext.Provider value={{ collection, setCollection }}>
						{currentStep === 0 ? <Step0 onImportRank={onImportRank} /> : null}
						{currentStep === 1 ? (
							<Step1
								canMoveOn={setCanMoveOn}
								canProceedToRanking={canMoveOn}
								onProceedToRanking={onProceedToRanking}
							/>
						) : null}
						{currentStep === 2 ? (
							<Step2 collection={collection} rankState={rankState} setRankState={setRankState} itemsById={itemsById} />
						) : null}
						{currentStep === 3 ? <Step3 ref={rankingJpegRef} rankState={rankState} itemsById={itemsById} /> : null}
						{currentStep === 2 ? (
							<Box position="fixed" left="-10000px" top={0} pointerEvents="none" aria-hidden>
								<Step3 ref={rankingJpegRef} rankState={rankState} itemsById={itemsById} />
							</Box>
						) : null}
					</CollectionContext.Provider>
				</Box>

				{currentStep === 2 || currentStep === 3 ? (
					<RankingSaveSlots exportGetSnapshot={exportGetSnapshot} onLoadSlot={onLoadSlot} />
				) : null}

				<ButtonGroup variant="solid" spacing={3} flexWrap="wrap">
					{currentStep >= 2 ? (
						<>
							<Button
								colorScheme="brand"
								onClick={() => {
									skipBeforeUnloadRef.current = true
									window.location.reload()
								}}
							>
								Start over
							</Button>
							<Button colorScheme="brand" variant="outline" onClick={onOpen}>
								Export JSON
							</Button>
							<Button
								colorScheme="brand"
								variant="outline"
								onClick={handleDownloadJpg}
								isLoading={isJpgExporting}
								loadingText="Exporting"
							>
								Download JPG
							</Button>
						</>
					) : (
						<>
							<Button
								onClick={() => setCurrentStep(currentStep - 1)}
								colorScheme="brand"
								variant="ghost"
								isDisabled={currentStep === 0}
							>
								{currentStep === 1 ? 'Import instead' : 'Back'}
							</Button>
							{currentStep === 1 ? (
								<Button colorScheme="brand" variant="outline" onClick={onOpenSavedSlots}>
									Saved slots
								</Button>
							) : null}
							{currentStep !== 1 ? (
								<Button onClick={() => setCurrentStep(currentStep + 1)} colorScheme="brand">
									{currentStep === 0 ? 'Continue to load' : 'Next'}
								</Button>
							) : null}
						</>
					)}
				</ButtonGroup>
			</VStack>

			<Modal isOpen={isOpen} onClose={onClose} motionPreset="none" size="lg">
				<ModalOverlay bg="blackAlpha.600" />
				<ModalContent rounded="xl" mx={4}>
					<ModalHeader>Exported collection</ModalHeader>
					<ModalCloseButton rounded="lg" />
					<ModalBody pb={6}>
						<Text fontSize="sm" color={exportModalMuted} mb={3}>
							Copy this JSON and store it somewhere safe. You can import it later from step 0 (schema version 2).
						</Text>
						<Textarea value={exportJson} isReadOnly fontSize="xs" rows={12} rounded="lg" />
						<Button onClick={onCopy} colorScheme="brand" mt={4} w={{ base: 'full', sm: 'auto' }}>
							{hasCopied ? 'Copied' : 'Copy to clipboard'}
						</Button>
					</ModalBody>
				</ModalContent>
			</Modal>

			<Modal isOpen={savedSlotsOpen} onClose={onCloseSavedSlots} motionPreset="none" size="lg">
				<ModalOverlay bg="blackAlpha.600" />
				<ModalContent rounded="xl" mx={4}>
					<ModalHeader>Saved slots</ModalHeader>
					<ModalCloseButton rounded="lg" />
					<ModalBody pb={6}>
						<RankingSaveSlots
							mode="loadOnly"
							onLoadSlot={onLoadSlotFromSavedModal}
							refreshWhenActive={savedSlotsOpen}
						/>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	)
}
